// src/components/admin/page-management/section-forms/TestimonialsForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaUploader from '../../shared/MediaUploader';
import { Plus, Trash2, ChevronDown, ChevronUp, StarIcon } from 'lucide-react';

const TestimonialsForm = ({ content, updateContent }) => {
  const [expandedTestimonialIndex, setExpandedTestimonialIndex] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  // Initialize testimonials if not present
  const testimonials = content?.testimonials || [
    {
      id: 1,
      name: 'Sarah Johnson',
      company: 'Bright Boutique',
      role: 'Owner',
      content: 'The custom shopping bags exceeded our expectations. Our customers love taking them home and they\'ve become a walking advertisement for our boutique!',
      rating: 5,
      imageUrl: '/images/testimonial1.jpg'
    },
    {
      id: 2,
      name: 'Michael Chen',
      company: 'Organic Delights',
      role: 'Marketing Director',
      content: 'We needed eco-friendly packaging that aligned with our brand values. Bag & Box delivered beautiful kraft paper bags that our customers appreciate for their sustainability.',
      rating: 5,
      imageUrl: '/images/testimonial2.jpg'
    },
    {
      id: 3,
      name: 'Robert Fernandez',
      company: 'Luxury Gifts',
      role: 'CEO',
      content: 'The premium gift boxes we ordered have elevated our entire unboxing experience. The attention to detail and quality of materials is exactly what our luxury brand needed.',
      rating: 5,
      imageUrl: '/images/testimonial3.jpg'
    }
  ];
  
  // Add a new testimonial
  const addTestimonial = () => {
    const newTestimonials = [...testimonials, {
      id: Date.now(), // Generate unique ID
      name: 'New Client',
      company: 'Company Name',
      role: 'Position',
      content: 'Add your testimonial content here...',
      rating: 5,
      imageUrl: ''
    }];
    
    updateContent('testimonials', newTestimonials);
    setExpandedTestimonialIndex(newTestimonials.length - 1);
  };
  
  // Remove a testimonial
  const removeTestimonial = (index) => {
    const newTestimonials = [...testimonials];
    newTestimonials.splice(index, 1);
    updateContent('testimonials', newTestimonials);
    
    if (expandedTestimonialIndex === index) {
      setExpandedTestimonialIndex(null);
    } else if (expandedTestimonialIndex > index) {
      setExpandedTestimonialIndex(expandedTestimonialIndex - 1);
    }
  };
  
  // Update a testimonial property
  const updateTestimonial = (index, property, value) => {
    const newTestimonials = [...testimonials];
    newTestimonials[index] = {
      ...newTestimonials[index],
      [property]: value
    };
    updateContent('testimonials', newTestimonials);
  };
  
  // Move a testimonial up or down
  const moveTestimonial = (index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === testimonials.length - 1)
    ) {
      return;
    }
    
    const newTestimonials = [...testimonials];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newTestimonials[index], newTestimonials[newIndex]] = [newTestimonials[newIndex], newTestimonials[index]];
    
    updateContent('testimonials', newTestimonials);
    
    // Update expanded index if needed
    if (expandedTestimonialIndex === index) {
      setExpandedTestimonialIndex(newIndex);
    } else if (expandedTestimonialIndex === newIndex) {
      setExpandedTestimonialIndex(index);
    }
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Testimonials Section</h3>
      
      {/* Section Content */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Section Content</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Title</label>
          <input
            type="text"
            value={content?.title || 'What Our Clients Say'}
            onChange={(e) => updateContent('title', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Subtitle</label>
          <textarea
            value={content?.subtitle || 'Hear from businesses who have transformed their packaging with us'}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            className="w-full p-2 border rounded"
            rows="2"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title Color</label>
            <ColorPicker
              color={content?.titleColor || '#111827'}
              onChange={(color) => updateContent('titleColor', color)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle Color</label>
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
          </div>
        </div>
      </div>
      
      {/* Layout & Display */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Layout & Display</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Layout Type</label>
          <select
            value={content?.layout || 'grid'}
            onChange={(e) => updateContent('layout', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="grid">Grid</option>
            <option value="slider">Slider</option>
          </select>
        </div>
        
        {content?.layout === 'grid' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Cards Per Row</label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1">Mobile</label>
                <select
                  value={content?.cardsPerRow?.mobile || 1}
                  onChange={(e) => updateContent('cardsPerRow', {
                    ...content?.cardsPerRow,
                    mobile: parseInt(e.target.value)
                  })}
                  className="w-full p-2 border rounded"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1">Tablet</label>
                <select
                  value={content?.cardsPerRow?.tablet || 2}
                  onChange={(e) => updateContent('cardsPerRow', {
                    ...content?.cardsPerRow,
                    tablet: parseInt(e.target.value)
                  })}
                  className="w-full p-2 border rounded"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1">Desktop</label>
                <select
                  value={content?.cardsPerRow?.desktop || 3}
                  onChange={(e) => updateContent('cardsPerRow', {
                    ...content?.cardsPerRow,
                    desktop: parseInt(e.target.value)
                  })}
                  className="w-full p-2 border rounded"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                </select>
              </div>
            </div>
          </div>
        )}
        
        {content?.layout === 'slider' && (
          <>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.autoplay !== false}
                  onChange={(e) => updateContent('autoplay', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Enable Autoplay</span>
              </label>
            </div>
            
            {content?.autoplay !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Autoplay Speed (ms)</label>
                <input
                  type="number"
                  value={content?.autoplaySpeed || 5000}
                  onChange={(e) => updateContent('autoplaySpeed', parseInt(e.target.value))}
                  className="w-full p-2 border rounded"
                  min="1000"
                  step="500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Time in milliseconds (1000ms = 1 second)
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.showSliderControls !== false}
                  onChange={(e) => updateContent('showSliderControls', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Slider Controls</span>
              </label>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.showSliderDots !== false}
                  onChange={(e) => updateContent('showSliderDots', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Slider Dots</span>
              </label>
            </div>
            
            {content?.showSliderDots !== false && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Dots Color</label>
                  <ColorPicker
                    color={content?.sliderDotsColor || '#d1d5db'}
                    onChange={(color) => updateContent('sliderDotsColor', color)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Active Dot Color</label>
                  <ColorPicker
                    color={content?.sliderDotsActiveColor || '#3b82f6'}
                    onChange={(color) => updateContent('sliderDotsActiveColor', color)}
                  />
                </div>
              </div>
            )}
          </>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.showImages !== false}
                onChange={(e) => updateContent('showImages', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Show Client Images</span>
            </label>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.showRating !== false}
                onChange={(e) => updateContent('showRating', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Show Star Ratings</span>
            </label>
          </div>
        </div>
        
        {content?.showImages !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Image Shape</label>
            <select
              value={content?.imageShape || 'circle'}
              onChange={(e) => updateContent('imageShape', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="circle">Circle</option>
              <option value="square">Square</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showQuoteIcon !== false}
              onChange={(e) => updateContent('showQuoteIcon', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Quote Icon</span>
          </label>
        </div>
        
        {content?.showQuoteIcon !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Quote Icon Color</label>
            <ColorPicker
              color={content?.quoteIconColor || 'rgba(0, 0, 0, 0.1)'}
              onChange={(color) => updateContent('quoteIconColor', color)}
            />
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showDivider !== false}
              onChange={(e) => updateContent('showDivider', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Divider Below Title</span>
          </label>
        </div>
        
        {content?.showDivider !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Divider Color</label>
            <ColorPicker
              color={content?.dividerColor || '#e5e7eb'}
              onChange={(color) => updateContent('dividerColor', color)}
            />
          </div>
        )}
      </div>
      
      {/* Colors & Styling */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Colors & Styling</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <ColorPicker
            color={content?.backgroundColor || '#ffffff'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
        </div>
        
        <button
          type="button"
          className="mb-4 text-blue-600 hover:text-blue-800 font-medium flex items-center"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          {showAdvancedSettings ? (
            <>
              <ChevronUp className="h-5 w-5 mr-1" />
              Hide Advanced Settings
            </>
          ) : (
            <>
              <ChevronDown className="h-5 w-5 mr-1" />
              Show Advanced Settings
            </>
          )}
        </button>
        
        {showAdvancedSettings && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Card Background</label>
                <ColorPicker
                  color={content?.cardBackgroundColor || '#f9fafb'}
                  onChange={(color) => updateContent('cardBackgroundColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Card Border</label>
                <ColorPicker
                  color={content?.cardBorderColor || '#e5e7eb'}
                  onChange={(color) => updateContent('cardBorderColor', color)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Text Color</label>
                <ColorPicker
                  color={content?.textColor || '#1f2937'}
                  onChange={(color) => updateContent('textColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company/Role Color</label>
                <ColorPicker
                  color={content?.companyColor || '#6b7280'}
                  onChange={(color) => updateContent('companyColor', color)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Star Color</label>
                <ColorPicker
                  color={content?.starColor || '#f59e0b'}
                  onChange={(color) => updateContent('starColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Color</label>
                <ColorPicker
                  color={content?.buttonColor || '#6b7280'}
                  onChange={(color) => updateContent('buttonColor', color)}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Hover Color</label>
              <ColorPicker
                color={content?.buttonHoverColor || '#4b5563'}
                onChange={(color) => updateContent('buttonHoverColor', color)}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Card Border Radius</label>
              <input
                type="text"
                value={content?.cardBorderRadius || '0.75rem'}
                onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 0.75rem, 12px"
              />
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.cardShadow !== false}
                  onChange={(e) => updateContent('cardShadow', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Card Shadow</span>
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* Spacing & Animation */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing & Animation</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Top Padding</label>
            <input
              type="text"
              value={content?.paddingTop || '4rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Padding</label>
            <input
              type="text"
              value={content?.paddingBottom || '4rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Margin</label>
            <input
              type="text"
              value={content?.marginBottom || '3rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 3rem, 48px"
            />
          </div>
        </div>
        
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="animation-enabled"
            checked={content?.animationEnabled !== false}
            onChange={(e) => updateContent('animationEnabled', e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="animation-enabled" className="text-sm">Enable Animation</label>
        </div>
      </div>
      
      {/* Testimonials */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Testimonials</h4>
        
        <div className="space-y-3">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id || index} 
              className="border rounded bg-white overflow-hidden"
            >
              <div 
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedTestimonialIndex(expandedTestimonialIndex === index ? null : index)}
              >
                <div className="flex items-center">
                  <span className="font-medium">{testimonial.name || `Testimonial ${index + 1}`}</span>
                  {testimonial.company && (
                    <span className="text-gray-500 ml-2">- {testimonial.company}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveTestimonial(index, 'up');
                    }}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveTestimonial(index, 'down');
                    }}
                    disabled={index === testimonials.length - 1}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTestimonial(index);
                    }}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  {expandedTestimonialIndex === index ? 
                    <ChevronUp className="h-5 w-5" /> : 
                    <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
              
              {expandedTestimonialIndex === index && (
                <div className="p-3 border-t">
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Client Name</label>
                    <input
                      type="text"
                      value={testimonial.name || ''}
                      onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Company</label>
                      <input
                        type="text"
                        value={testimonial.company || ''}
                        onChange={(e) => updateTestimonial(index, 'company', e.target.value)}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Role/Position</label>
                      <input
                        type="text"
                        value={testimonial.role || ''}
                        onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Testimonial Content</label>
                    <textarea
                      value={testimonial.content || ''}
                      onChange={(e) => updateTestimonial(index, 'content', e.target.value)}
                      className="w-full p-2 border rounded"
                      rows="3"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Rating (1-5 stars)</label>
                    <select
                      value={testimonial.rating || 5}
                      onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                      className="w-full p-2 border rounded"
                    >
                      <option value={1}>1 Star</option>
                      <option value={2}>2 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Client Image</label>
                    <MediaUploader
                      type="image"
                      value={testimonial.imageUrl || ''}
                      onUploadComplete={(url) => updateTestimonial(index, 'imageUrl', url)}
                      onRemove={() => updateTestimonial(index, 'imageUrl', '')}
                      className="mb-2"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <button
          type="button"
          className="mt-4 flex items-center justify-center w-full p-2 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400"
          onClick={addTestimonial}
        >
          <Plus className="h-5 w-5 mr-1" />
          Add Testimonial
        </button>
      </div>
    </div>
  );
};

export default TestimonialsForm;